import { RefObject, useLayoutEffect, useMemo, useState } from 'react';

export interface GridLayout {
  columns: number;
  cellSize: number;
  gap: number;
}

const MIN_CELL = 28;
const GAP = 8;

/** Считает квадратную упаковку по числу клеток и размеру сцены. */
export function computeGridLayout(
  itemCount: number,
  width: number,
  height: number
): GridLayout {
  if (itemCount <= 0 || width <= 0 || height <= 0) {
    return { columns: 1, cellSize: 48, gap: GAP };
  }

  let bestColumns = 1;
  let bestCell = 0;

  for (let columns = 1; columns <= itemCount; columns += 1) {
    const rows = Math.ceil(itemCount / columns);
    const cellWidth = (width - (columns - 1) * GAP) / columns;
    const cellHeight = (height - (rows - 1) * GAP) / rows;
    const cell = Math.min(cellWidth, cellHeight);
    if (cell > bestCell) {
      bestCell = cell;
      bestColumns = columns;
    }
  }

  return {
    columns: bestColumns,
    cellSize: Math.max(MIN_CELL, Math.floor(bestCell)),
    gap: GAP,
  };
}

/** Следит за размером сцены и сразу считает упаковку под текущее число клеток. */
export function useGridLayout(
  itemCount: number,
  stageRef: RefObject<HTMLElement | null>
): GridLayout {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const measure = () => {
      setSize({ width: stage.clientWidth, height: stage.clientHeight });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [stageRef]);

  return useMemo(
    () => computeGridLayout(itemCount, size.width, size.height),
    [itemCount, size.width, size.height]
  );
}
